import {
    AGGREGATE_NAMES,
    API_ROUTES,
    BACKEND_RESOURCE_NAMES,
    CHAT_COMMANDS,
    CHAT_READ_CURSOR_COMMANDS,
    CHAT_RESOLVERS,
    CORPORATION_COMMANDS,
    CORPORATION_RESOLVERS,
    DIVISION_COMMANDS,
    DIVISION_RESOLVERS,
    LOAN_REQUEST_APPLICANT_EDITING_COMMANDS,
    LOAN_REQUEST_COMMANDS,
    LOAN_REQUEST_RESOLVERS,
    PARTNER_RESOLVERS,
    SYSTEM_VARIABLE_COMMANDS,
    SYSTEM_VARIABLE_RESOLVERS,
    TEAM_COMMANDS,
    TEAM_RESOLVERS,
    TEMPLATE_COMMANDS,
    TEMPLATE_RESOLVERS,
    USER_COMMANDS,
    USER_RESOLVERS,
    WABA_RESOLVERS,
} from 'cpool-constants';
import type { Resolver } from 'cpool-schemas';
import { schemaId } from 'cpool-schemas';
import { buildSchemaShape, buildSchemaShapeWithDiscriminator } from 'cpool-utils';
import { z } from 'zod';

const buildBooleanRecordSchema = <T extends Record<string, unknown>>(obj: T) =>
    buildSchemaShape({ getSchema: () => z.boolean(), obj });

const apiSchemaShape = buildBooleanRecordSchema(API_ROUTES);

const applicantEditingSchema = z.object({
    applicant1: z.boolean(),
    applicant2: z.boolean(),
});

const loanRequestCommandsSchemaShape = buildSchemaShapeWithDiscriminator({
    keysForSchema1: LOAN_REQUEST_APPLICANT_EDITING_COMMANDS,
    obj: LOAN_REQUEST_COMMANDS,
    schema1: applicantEditingSchema,
    schema2: z.boolean(),
});

const commandsSchemaShape = {
    [AGGREGATE_NAMES.CHAT]: z.object(buildBooleanRecordSchema(CHAT_COMMANDS)),
    [AGGREGATE_NAMES.CHAT_READ_CURSOR]: z.object(buildBooleanRecordSchema(CHAT_READ_CURSOR_COMMANDS)),
    [AGGREGATE_NAMES.CORPORATION]: z.object(buildBooleanRecordSchema(CORPORATION_COMMANDS)),
    [AGGREGATE_NAMES.DIVISION]: z.object(buildBooleanRecordSchema(DIVISION_COMMANDS)),
    [AGGREGATE_NAMES.LOAN_REQUEST]: z.object(loanRequestCommandsSchemaShape),
    [AGGREGATE_NAMES.PARTNER]: z.object({}),
    [AGGREGATE_NAMES.SYSTEM_VARIABLE]: z.object(buildBooleanRecordSchema(SYSTEM_VARIABLE_COMMANDS)),
    [AGGREGATE_NAMES.TEAM]: z.object(buildBooleanRecordSchema(TEAM_COMMANDS)),
    [AGGREGATE_NAMES.TEMPLATE]: z.object(buildBooleanRecordSchema(TEMPLATE_COMMANDS)),
    [AGGREGATE_NAMES.USER]: z.object(buildBooleanRecordSchema(USER_COMMANDS)),
    [AGGREGATE_NAMES.WABA]: z.object({}),
} as const;

const resolversSchemaShape = {
    // ChatReadCursor has no resolvers of its own - read access to chats/messages is exposed
    // under the chat readModel (CHAT_RESOLVERS) instead.
    [AGGREGATE_NAMES.CHAT]: z.object(buildBooleanRecordSchema(CHAT_RESOLVERS)),
    [AGGREGATE_NAMES.CHAT_READ_CURSOR]: z.object({}),
    [AGGREGATE_NAMES.CORPORATION]: z.object(buildBooleanRecordSchema(CORPORATION_RESOLVERS)),
    [AGGREGATE_NAMES.DIVISION]: z.object(buildBooleanRecordSchema(DIVISION_RESOLVERS)),
    [AGGREGATE_NAMES.LOAN_REQUEST]: z.object(buildBooleanRecordSchema(LOAN_REQUEST_RESOLVERS)),
    [AGGREGATE_NAMES.PARTNER]: z.object(buildBooleanRecordSchema(PARTNER_RESOLVERS)),
    [AGGREGATE_NAMES.SYSTEM_VARIABLE]: z.object(buildBooleanRecordSchema(SYSTEM_VARIABLE_RESOLVERS)),
    [AGGREGATE_NAMES.TEAM]: z.object(buildBooleanRecordSchema(TEAM_RESOLVERS)),
    [AGGREGATE_NAMES.TEMPLATE]: z.object(buildBooleanRecordSchema(TEMPLATE_RESOLVERS)),
    [AGGREGATE_NAMES.USER]: z.object(buildBooleanRecordSchema(USER_RESOLVERS)),
    [AGGREGATE_NAMES.WABA]: z.object(buildBooleanRecordSchema(WABA_RESOLVERS)),
} as const;

export const schemaUserResolver_GetAclPermissionsArgs = z.object({
    actionFilter: z.record(z.string(), z.array(z.string())).optional(),
    chatLinkInfo: z
        .object({
            chatId: z.string(),
            loanRequestIds: z.array(z.string()),
            // optional: empty arrays drop out of the query string.
            partnerIds: z.array(schemaId).optional(),
            // optional: null (BSUID) drops out of the query string.
            phone: z.string().optional(),
            phoneNumberId: z.string(),
        })
        .optional(),
    loanRequestId: z.string().optional(),
    userId: schemaId.optional(),
});

export type SchemaUserResolver_GetAclPermissionsArgs = z.infer<typeof schemaUserResolver_GetAclPermissionsArgs>;

export const schemaUserResolver_GetAclPermissions = z.object({
    [BACKEND_RESOURCE_NAMES.API]: z.object(apiSchemaShape),
    [BACKEND_RESOURCE_NAMES.COMMANDS]: z.object(commandsSchemaShape),
    [BACKEND_RESOURCE_NAMES.RESOLVERS]: z.object(resolversSchemaShape),
});

export type SchemaUserResolver_GetAclPermissions = z.infer<typeof schemaUserResolver_GetAclPermissions>;

export type UserResolver_GetAclPermissions = Resolver<
    SchemaUserResolver_GetAclPermissionsArgs,
    SchemaUserResolver_GetAclPermissions
>;
